import { useState, type ReactNode } from 'react';
import { Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  EntitySharePanel,
  getEntityShareLabel,
  type ShareEntityType,
} from './EntitySharePanel';

interface EntityShareActionProps {
  entityType: ShareEntityType;
  publicUrl?: string | null;
  embedSnippet?: string | null;
  socialAction?: ReactNode;
  socialDescription?: string;
  buttonLabel?: 'Dela' | 'Dela sidan';
  buttonClassName?: string;
}

export function EntityShareAction({
  entityType,
  publicUrl,
  embedSnippet,
  socialAction,
  socialDescription,
  buttonLabel = 'Dela',
  buttonClassName,
}: EntityShareActionProps) {
  const [open, setOpen] = useState(false);
  const hasCapabilities = Boolean(publicUrl || embedSnippet || socialAction);

  if (!hasCapabilities) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={buttonClassName ?? 'gap-1.5'}
        onClick={() => setOpen(true)}
      >
        <Share2 className="h-4 w-4" />
        {buttonLabel}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-base font-semibold">Dela {getEntityShareLabel(entityType)}</SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              Välj hur du vill dela via länk, sociala medier eller inbäddning.
            </SheetDescription>
          </SheetHeader>

          <EntitySharePanel
            variant="flat"
            entityType={entityType}
            publicUrl={publicUrl}
            embedSnippet={embedSnippet}
            socialAction={socialAction}
            socialDescription={socialDescription}
            className="mt-4"
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
